/**
 * The four publish targets as data — the fields each one asks for, the setup
 * guide beside it, and the two mappings between a stored `settings` object and
 * the form that edits it.
 *
 * Settings consolidation (2026-09-15): moved whole from `pages/seo/PublishTargets`.
 * Pure — no React — so the summary line on the publishing page and the setup
 * card under Settings read the same vocabulary.
 */

/* --- the four targets, as data ------------------------------------------------- */

export interface TargetField {
  name: string
  label: string
  help?: string
  required?: boolean
  kind: 'text' | 'select' | 'number' | 'ints' | 'words' | 'switch'
  /**
   * What the field means when the tenant has stored nothing — **the value the
   * publisher's own `Config.of` will fall back to**, not the first option.
   *
   * Only `select` reads it, and it exists because of a live defect this fixed.
   * `formOf` seeds an absent value as `''`; a `<select>` whose value matches no
   * option falls back to `selectedIndex 0`, so WordPress's *Publish as* read
   * **“Draft — lands in your editorial queue”** on every fresh setup — while
   * `settingsOf` omitted the untouched field and `WordPressConfig.of` applied
   * `STATUS_PUBLISH`. The screen said the article would wait in a queue and the
   * article went live on the tenant's public website.
   *
   * Text fields do not take one: their `placeholder` already shows the default
   * without claiming it has been chosen, and an empty text box is honestly
   * empty. A select has no such state — it always displays *something*, so
   * whatever it displays has to be true.
   */
  defaultValue?: string
  placeholder?: string
  mono?: boolean
  options?: readonly { value: string; label: string }[]
  /** Nested under `settings.fields` — Webflow's collection field slugs. */
  group?: 'fields'
}

/**
 * One numbered step in *How to set this up*.
 *
 * **SEO-UI-118.** The screen had field-level `help` and nothing above it, which
 * assumes the reader already knows what the fields are *for*. They frequently
 * do not: the defect that named this was a tenant who pasted their own SSH
 * public key into **Host key** — a field whose help text says to run
 * `ssh-keyscan`, read by somebody who had just generated a keypair and had two
 * plausible keys in front of them. Field help answers *what goes in this box*;
 * these answer *where do I get it*, which is the question actually being asked.
 *
 * `command` is rendered as a copyable line. It is the difference between an
 * instruction and a thing the reader can do, and every one below was run before
 * it was written down.
 */
interface SetupStep {
  text: string
  command?: string
  /** A field this step produces, so the reader can tie the two together. */
  fills?: string
}

export interface TargetSpec {
  id: string
  label: string
  logo: string
  blurb: string
  credentialLabel: string
  credentialHelp: string
  /**
   * Whether this target authenticates to anything. `true` on `download`, which
   * reaches no network — so no credential box is rendered, and the note under
   * the form must not say a publish would be refused.
   *
   * **Read from the spec rather than from `credential_required` on the
   * response**, and the difference matters for one render: pressing *Set up*
   * changes the target on screen immediately, and the query answering about it
   * lands a frame later. Trusting the response in that frame is what puts a
   * *Deploy key* box under a *Files to download* heading — the same one-frame
   * confusion `seededFrom` exists to prevent, and the reason `pending` is
   * computed the way it is below. The server states the same fact in
   * `PublishTargetOut.credential_required` for any other consumer of the API.
   */
  credentialFree?: boolean
  /** What to do before filling the form in. Empty for a target that needs nothing. */
  steps: readonly SetupStep[]
  /**
   * What the check button says. *Send a test post* is the mockup's label and
   * `Publisher.check` explains at length why it is deliberately not one — but on
   * `download` it is not even a metaphor, since there is no far end to send
   * anything to, so that target renames it rather than lying twice.
   */
  checkLabel?: string
  fields: readonly TargetField[]
  /**
   * Render the credential box immediately after this field, rather than after
   * all of them (SEO-UI-100).
   *
   * WordPress is the case the design draws: the application password belongs
   * with the address and the user name it authenticates, not after the
   * categories an article is filed under. Omitted on the other three, whose
   * settings are all *where does the content go* — there is no group for the
   * credential to be separated from, so last is where it reads best.
   */
  credentialAfter?: string
}

/**
 * Every field name, default and bound below is read off the publisher that will
 * use it — `app/seo/gitpush.py`, `wordpress.py`, `webflow.py` and `shopify.py`,
 * via `PublishTargetIn`'s own description of what each target takes. Nothing
 * here invents a setting: a key this form sends that no `Config.of` reads would
 * be written to the row and silently ignored for ever.
 *
 * A function of the product name rather than a constant (spec: *Branding*):
 * the setup steps tell a tenant what to call the WordPress user and the
 * Shopify app, and on a white-labelled host that is the organisation's own
 * name, not AugRank's. Built once per render of `TargetSetup`, memoised on
 * the name.
 */
export function targetsFor(product: string): readonly TargetSpec[] {
  return [
    {
      id: 'git',
      label: 'Git repository',
      logo: 'git',
      blurb: `For Next.js, Hugo, Astro — ${product} commits a Markdown file and your build publishes it.`,
      credentialLabel: 'Deploy key',
      credentialHelp:
        'The private half of an SSH deploy key with write access. Paste the whole key, including the BEGIN and END lines.',
      steps: [
        {
          text: 'Make a key pair on your computer. It writes two files: augrank_deploy (the private half) and augrank_deploy.pub (the public half). Press Enter when it asks for a passphrase — a key with one cannot be used unattended.',
          command: 'ssh-keygen -t ed25519 -N "" -C augrank -f ~/.ssh/augrank_deploy',
        },
        {
          text: 'Print the PUBLIC half and copy it. This is the one that goes to GitHub, GitLab or Bitbucket — never into this form.',
          command: 'cat ~/.ssh/augrank_deploy.pub',
        },
        {
          text: 'In your repository on GitHub: Settings → Deploy keys → Add deploy key. Paste the public half, and TICK “Allow write access” — without it the push is refused. On GitLab it is Settings → Repository → Deploy keys; on Bitbucket, Repository settings → Access keys.',
        },
        {
          text: `Print the PRIVATE half and paste all of it — including the BEGIN and END lines — into Deploy key below. ${product} writes it to encrypted storage and can never show it to you again.`,
          command: 'cat ~/.ssh/augrank_deploy',
          fills: 'Deploy key',
        },
        {
          text: `Ask the host for ITS key, and paste what comes back WITHOUT the host name at the front — the part starting ssh-ed25519. This is the server’s identity, not yours: it is what lets ${product} tell your repository host from something pretending to be it. Change github.com if you are on GitLab or Bitbucket.`,
          command: 'ssh-keyscan -t ed25519 github.com',
          fills: 'Host key',
        },
        {
          text: 'Copy the repository’s SSH address from its Code button — the git@… form, not the https:// one — into Repository below.',
          fills: 'Repository',
        },
      ],
      fields: [
        {
          name: 'repo_url',
          label: 'Repository',
          required: true,
          kind: 'text',
          mono: true,
          placeholder: 'git@github.com:owner/repo.git',
          help: 'An SSH remote — that is what a deploy key authenticates against.',
        },
        { name: 'branch', label: 'Branch', kind: 'text', placeholder: 'main' },
        {
          name: 'path_template',
          label: 'Where the file goes',
          kind: 'text',
          mono: true,
          placeholder: 'content/blog/{slug}.md',
          help: 'Relative to the repository root, and it must contain {slug} — without it every article overwrites the same file.',
        },
        {
          name: 'host_key',
          label: 'Host key',
          kind: 'text',
          mono: true,
          placeholder: 'ssh-ed25519 AAAA…',
          help: 'Run ssh-keyscan -t ed25519 on the repository’s host and paste the part after the host name. Required for an SSH remote, so the push can verify which server it reached.',
        },
        {
          name: 'url_template',
          label: 'Public URL',
          kind: 'text',
          placeholder: 'https://example.com/blog/{slug}',
          help: `Optional. A push cannot know how your site routes, so ${product} shows no link at all rather than a wrong one.`,
        },
        // Rendered although `PublishTargetIn`'s description does not list them,
        // and that is the point: `GitConfig` reads both, and `write_target`
        // replaces a target's settings object wholesale. A form that omitted a
        // field the publisher uses would silently drop whatever was there.
        { name: 'author_name', label: 'Commit author', kind: 'text', placeholder: product },
        {
          name: 'author_email',
          label: 'Commit email',
          kind: 'text',
          placeholder: 'seo@augrank.com',
          help: 'The email address on each commit, beside the author name above. Both show in your repository’s history.',
        },
      ],
    },
    {
      id: 'wordpress',
      label: 'WordPress',
      logo: 'W',
      blurb: `Posts through the WordPress REST API, as a user you create for ${product}.`,
      credentialLabel: 'Application password',
      credentialHelp:
        'Created under Users → Profile → Application Passwords in WordPress. Not the account’s login password.',
      steps: [
        {
          text: `In WordPress: Users → Add New. Make an account for ${product} with the Editor role — Editor can publish posts; Author cannot publish anyone else’s, and Administrator hands over far more than is needed.`,
        },
        {
          text: `Sign in as that new account, open Users → Profile, scroll to Application Passwords, type “${product}” as the name and press Add New Application Password.`,
        },
        {
          text: 'WordPress shows a password of 24 characters in six groups ONCE. Copy it now — closing the page loses it — and paste it below. Spaces in it are fine.',
          fills: 'Application password',
        },
        {
          text: 'Put the site’s own address in Site URL — https://example.com, not the /wp-json path and not the /wp-admin one — and that account’s user name in Username.',
          fills: 'Site URL',
        },
        {
          text: 'If nothing works and the password is definitely right, your host may be stripping the Authorization header. Ask them to allow it for the REST API; it is a one-line change and a common default on shared hosting.',
        },
      ],
      // SEO-UI-100. Site URL, Username, Application password — then *Publish as*,
      // Categories and Author id. The design's order, and the group it makes:
      // three fields that say who AugRank is on the site, then the ones that say
      // what it does there.
      credentialAfter: 'username',
      fields: [
        {
          name: 'base_url',
          label: 'Site URL',
          required: true,
          kind: 'text',
          placeholder: 'https://example.com',
        },
        {
          name: 'username',
          label: 'Username',
          required: true,
          kind: 'text',
        },
        {
          name: 'status',
          label: 'Publish as',
          kind: 'select',
          // `WordPressConfig.status` — `STATUS_PUBLISH`. Stated here because the
          // control cannot show "nothing chosen", so it must show what will happen.
          defaultValue: 'publish',
          // The drawing's two labels (`seo.html:1396`); approval is a separate
          // gate either way — this is only what WordPress does with the post.
          options: [
            { value: 'draft', label: 'Draft (safest)' },
            { value: 'publish', label: 'Published' },
          ],
        },
        {
          name: 'category_ids',
          label: 'Categories',
          kind: 'ints',
          placeholder: '12, 34',
          help: 'WordPress term ids, separated by commas. Leave empty for the site’s default category.',
        },
        {
          name: 'author_id',
          label: 'Author id',
          kind: 'number',
          help: 'Leave empty to post as the application password’s own user, which is the only account that always has permission.',
        },
      ],
    },
    {
      id: 'webflow',
      label: 'Webflow',
      logo: 'Wf',
      blurb: 'CMS collection items via the Webflow API.',
      credentialLabel: 'API token',
      credentialHelp: 'A Webflow site token with CMS write access.',
      steps: [
        {
          text: 'In Webflow: Site settings → Apps & integrations → API access → Generate API token. Give it the CMS scope with read AND write — a read-only token authenticates perfectly and then refuses every publish.',
        },
        {
          text: 'Copy the token as soon as it appears. Webflow shows it once, and paste it below.',
          fills: 'API token',
        },
        {
          text: 'Open the Designer, click the CMS icon and select your blog collection. The 24-character id is the last part of the address bar — copy it into Collection id.',
          fills: 'Collection id',
        },
        {
          text: 'The four field slugs below are Webflow’s own blog template. Change one only if your collection renamed that field — the slug is under the field’s settings in the collection editor, not its display name.',
        },
      ],
      fields: [
        {
          name: 'collection_id',
          label: 'Collection id',
          required: true,
          kind: 'text',
          mono: true,
          help: 'The 24-character id in the collection’s URL in the Webflow designer.',
        },
        {
          name: 'title',
          group: 'fields',
          label: 'Title field',
          kind: 'text',
          mono: true,
          placeholder: 'name',
        },
        {
          name: 'slug',
          group: 'fields',
          label: 'Slug field',
          kind: 'text',
          mono: true,
          placeholder: 'slug',
        },
        {
          name: 'body',
          group: 'fields',
          label: 'Body field',
          kind: 'text',
          mono: true,
          placeholder: 'post-body',
        },
        {
          name: 'summary',
          group: 'fields',
          label: 'Summary field',
          kind: 'text',
          mono: true,
          placeholder: 'post-summary',
          help: 'The four slugs above are Webflow’s own blog template. Change one only if your collection renamed it.',
        },
        {
          name: 'url_template',
          label: 'Public URL',
          kind: 'text',
          placeholder: 'https://example.com/blog/{slug}',
          help: `Webflow does not return a public address for a CMS item, so this is the only way ${product} can offer a link to it.`,
        },
      ],
    },
    {
      id: 'shopify',
      label: 'Shopify',
      logo: 'S',
      blurb: 'Blog articles through the Shopify Admin API.',
      credentialLabel: 'Admin API token',
      credentialHelp: 'A custom app’s Admin API access token with write_content scope.',
      steps: [
        {
          text: `In Shopify admin: Settings → Apps and sales channels → Develop apps → Create an app. Call it ${product}.`,
        },
        {
          text: 'Open Configuration → Admin API integration and tick write_content (and read_content). Nothing else — those two are the whole of what publishing a blog article needs.',
        },
        {
          text: 'Press Install app, then Reveal token once. It starts shpat_ and is shown a single time; paste it below.',
          fills: 'Admin API token',
        },
        {
          text: 'Shop domain is the myshopify.com form from the address bar of your admin, not your storefront’s custom domain.',
          fills: 'Shop domain',
        },
        {
          text: 'Open Content → Blog posts → Manage blogs and click your blog. The number at the end of that page’s address is the Blog id.',
          fills: 'Blog id',
        },
      ],
      fields: [
        {
          name: 'shop_domain',
          label: 'Shop domain',
          required: true,
          kind: 'text',
          mono: true,
          placeholder: 'example.myshopify.com',
          help: 'The myshopify.com form, not the storefront’s custom domain.',
        },
        {
          name: 'blog_id',
          label: 'Blog id',
          required: true,
          kind: 'text',
          mono: true,
          help: 'The numeric id at the end of the blog’s admin URL.',
        },
        { name: 'tags', label: 'Tags', kind: 'words', placeholder: 'guides, pricing' },
        { name: 'author', label: 'Byline', kind: 'text', placeholder: product },
        {
          name: 'published',
          label: 'Visible as soon as it is created',
          kind: 'switch',
          help: 'Off leaves the article in the store’s blog as unpublished.',
        },
      ],
    },
    {
      id: 'download',
      label: 'Files to download',
      logo: '↓',
      blurb: `${product} writes the file and you put it wherever you like. No repository access, no CMS account, no token.`,
      credentialFree: true,
      credentialLabel: '',
      credentialHelp: '',
      checkLabel: 'What will I get?',
      // Nothing to obtain, so nothing to explain — but the panel still renders,
      // because *"there is no step 1"* is itself the answer a tenant weighing this
      // against handing over a deploy key came here for.
      steps: [
        {
          text: 'There is nothing to set up and nothing to hand over. Choose a format below, press Save, and every article you approve and publish gets a Download button in the list at the bottom of this screen.',
        },
        {
          text: `Pick Markdown if a developer will drop the file into a site build — Hugo, Astro, Next.js and Jekyll all read the header ${product} writes. Pick HTML if you are pasting into an editor, or just want to read it first.`,
          fills: 'File format',
        },
        {
          text: 'Undo on a published article withdraws its file as well. The download stops working, which is what makes this a publishing decision rather than an export button.',
        },
      ],
      fields: [
        {
          name: 'format',
          label: 'File format',
          kind: 'select',
          // `files.FORMAT_MARKDOWN`, which `DownloadConfig.of` falls back to.
          defaultValue: 'markdown',
          options: [
            { value: 'markdown', label: 'Markdown — for a site build' },
            { value: 'html', label: 'HTML — for pasting, or for reading' },
          ],
          help: 'Markdown is what Hugo, Astro, Next.js and Jekyll read. HTML is a whole page, with the title, the description and the structured data already in it.',
        },
        {
          name: 'front_matter',
          label: 'Include the header a site build reads',
          kind: 'switch',
          help: 'The eight lines at the top of a Markdown file — title, slug, description, date. A build needs them to place the article on its index. Turn this off if you are pasting the text into an editor. Ignored for HTML, which carries the same facts in its head.',
        },
      ],
    },
  ]
}

/** The chosen target, falling back to git — which is the first of the four and
 *  the one a static-site tenant is likeliest to want. `targetsFor` returns a
 *  literal, so the fallback is total by construction; the throw is what keeps
 *  that provable to the type checker without a non-null assertion. */
export function specFor(targets: readonly TargetSpec[], id: string | null): TargetSpec {
  const found = targets.find((target) => target.id === id) ?? targets[0]
  if (found === undefined) throw new Error('targetsFor answered nothing')
  return found
}

/* --- the form's values, which are strings until they are sent ------------------ */

export type FormValue = string | boolean
export type Form = Record<string, FormValue>

export function keyOf(field: TargetField): string {
  return field.group === undefined ? field.name : `${field.group}.${field.name}`
}

/**
 * A stored settings object as the form holds it.
 *
 * Everything is a string except a switch, because that is what an `<input>`
 * gives back, and converting on the way in and out in one place is what keeps
 * `1, 2` from being sent as the string `"1, 2"` to a field the server parses as
 * a list of ints. A switch defaults to **on**, matching `ShopifyConfig.published`
 * — `raw.get("published") is not False` — so an unset field and a ticked box
 * mean the same thing on both sides.
 */
export function formOf(spec: TargetSpec, settings: Record<string, unknown>): Form {
  const form: Form = {}
  for (const field of spec.fields) {
    const source =
      field.group === undefined
        ? settings
        : ((settings[field.group] as Record<string, unknown> | undefined) ?? {})
    const value = source[field.name]
    if (field.kind === 'switch') {
      form[keyOf(field)] = value !== false
    } else if (Array.isArray(value)) {
      form[keyOf(field)] = value.map((item) => String(item)).join(', ')
    } else if (typeof value === 'string' || typeof value === 'number') {
      form[keyOf(field)] = String(value)
    } else {
      // `defaultValue` for a select, `''` for everything else — see
      // `TargetField.defaultValue` for the defect that distinction fixes.
      form[keyOf(field)] = field.defaultValue ?? ''
    }
  }
  return form
}

/** The form as `PublishTargetIn.settings`. An empty field is omitted, never sent
 *  as `""` — the publishers read a missing key as "use the default" and an empty
 *  string as a value, and the two produce different configurations. */
export function settingsOf(spec: TargetSpec, form: Form): Record<string, unknown> {
  const settings: Record<string, unknown> = {}
  const nested: Record<string, string> = {}
  for (const field of spec.fields) {
    const raw = form[keyOf(field)]
    if (field.kind === 'switch') {
      settings[field.name] = raw === true
      continue
    }
    const text = typeof raw === 'string' ? raw.trim() : ''
    if (text === '') continue
    let value: unknown = text
    if (field.kind === 'number') value = Number(text)
    if (field.kind === 'ints') {
      value = text
        .split(',')
        .map((item) => Number(item.trim()))
        .filter((item) => Number.isFinite(item))
    }
    if (field.kind === 'words') {
      value = text
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item !== '')
    }
    if (field.group === undefined) settings[field.name] = value
    else nested[field.name] = String(value)
  }
  if (Object.keys(nested).length > 0) settings.fields = nested
  return settings
}
